import React, { useEffect, useState } from "react";

import { UseOutletContextCompany } from "../Company";
import { StateLanguage } from "../../../../languages/config/StateLanguage";
import { SettingsDA } from "../../../../services/admin/settings.service";
import { Error } from "../../../../models/error.model";
import { PaymentDeadline, PaymentDeadlinesData } from "../../../../models/settings.payment-deadlines.model";
import { MeasureUnit, MeasureUnitsData } from "../../../../models/settings.measure-units.model";
import { Feature, FeaturesData } from "../../../../models/settings.features.model";
import { DunningPolicy, DunningPoliciesData } from "../../../../models/settings.dunning-policies.model";
import { GlobalSwal } from "../../../../styles/swal.style";
import { getError } from "../../../../languages/translations/response";
import { modalShow } from "../../../../utils/modal.util";
import { errorWebHandler } from "../../../../scripts/error.web.handler.script";
import { usePagination } from "../../../../hooks/usePagination";

import AppLoading from "../../../../components/Loading";
import AppModalSettingsDeadline from "../../../../components/modal/ModalDeadline";
import AppModalSettingsMeasureUnit from "../../../../components/modal/ModalMeasureUnit";
import AppModalSettingsFeature from "../../../../components/modal/ModalFeature";
import AppModalDunningPolicy from "../../../../components/modal/ModalDunningPolicy";

import AppPagination from "../../../../components/element/ElementPagination";


export interface AppCompanyUpdateGeneralProps {}

let errorResponse: Error;

const AppCompanyUpdateGeneral: React.FunctionComponent<AppCompanyUpdateGeneralProps> = (props) => {
  const { appServiceForUser, companyForUser } = UseOutletContextCompany();
  const { lang } = StateLanguage();

  const [deadlines, setDeadlines] = useState<PaymentDeadline[]>([]);
  const [units, setUnits] = useState<MeasureUnit[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);

  const [dunningPolicies, setDunningPolicies] = useState<DunningPolicy[]>([]);

  const [selectedDeadline, setSelectedDeadline] = useState<PaymentDeadline | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<MeasureUnit | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<DunningPolicy | null>(null);
  const { pagination: deadlinePag, setPage: setDeadlinePage, setPaginationFromBackend: setDeadlinePagData } = usePagination();
  const { pagination: unitPag, setPage: setUnitPage, setPaginationFromBackend: setUnitPagData } = usePagination();
  const { pagination: featurePag, setPage: setFeaturePage, setPaginationFromBackend: setFeaturePagData } = usePagination();
  const { pagination: policyPag, setPage: setPolicyPage, setPaginationFromBackend: setPolicyPagData } = usePagination();

  // ── LOADERS ───────────────────────────────────────────────────────────────

  const loadDeadlines = async (page: number = 1) => {
    if (companyForUser) {
      await SettingsDA.getSettingPaymentDeadlines(companyForUser.id, page, deadlinePag.limit)
        .then((response) => {
          if (response.status === 200) {
            const data = response.data as PaymentDeadlinesData;
            setDeadlines(data.items ?? []);
            setDeadlinePagData(data.pagination);
          } else {
            errorResponse = response.data;
            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: "error",
              confirmButtonText: lang.labels.okGotIt,
            });
          }
        })
        .catch((error) => {
          errorWebHandler(lang, error);
        });
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: "error",
        showConfirmButton: false,
        timer: 2200,
      }).then(() => {});
    }
  };

  const loadUnits = async (page: number = 1) => {
    if (companyForUser) {
      await SettingsDA.getSettingMeasureUnits(companyForUser.id, page, unitPag.limit)
        .then((response) => {
          if (response.status === 200) {
            const data = response.data as MeasureUnitsData;
            setUnits(data.items ?? []);
            setUnitPagData(data.pagination);
          } else {
            errorResponse = response.data;
            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: "error",
              confirmButtonText: lang.labels.okGotIt,
            });
          }
        })
        .catch((error) => {
          errorWebHandler(lang, error);
        });
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: "error",
        showConfirmButton: false,
        timer: 2200,
      }).then(() => {});
    }
  };

  const loadFeatures = async (page: number = 1) => {
    if (companyForUser) {
      await SettingsDA.getSettingFeatures(companyForUser.id, page, featurePag.limit)
        .then((response) => {
          if (response.status === 200) {
            const data = response.data as FeaturesData;
            setFeatures(data.items ?? []);
            setFeaturePagData(data.pagination);
          } else {
            errorResponse = response.data;
            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: "error",
              confirmButtonText: lang.labels.okGotIt,
            });
          }
        })
        .catch((error) => {
          errorWebHandler(lang, error);
        });
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: "error",
        showConfirmButton: false,
        timer: 2200,
      }).then(() => {});
    }
  };

  // ── PAYMENT DEADLINE ACTIONS ──────────────────────────────────────────────────────

  const executeOpenDeadlineCreate = () => {
    setSelectedDeadline(null);
    modalShow("modal-settings-deadline");
  };

  const executeOpenDeadlineEdit = (item: PaymentDeadline) => {
    setSelectedDeadline(item);
    modalShow("modal-settings-deadline");
  };

  const executeDeleteDeadline = async (item: PaymentDeadline) => {
    GlobalSwal.fire({
      title: lang.labels.areYouSureYouWantToDeleteThisRecord,
      text: lang.labels.youWontBeAbleToRevertThis,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDeleteIt,
      cancelButtonText: lang.labels.cancel,
    }).then(async (result) => {
      if (companyForUser && result.isConfirmed) {
        await SettingsDA.deleteSettingPaymentDeadline(companyForUser.id,item.id)
          .then((response) => {
            if (response.status === 200) {
              const prevPage = deadlines?.length === 1 && deadlinePag.page > 1 ? deadlinePag.page - 1 : deadlinePag.page;
              loadDeadlines(prevPage);
            } else {
              errorResponse = response.data;
              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: "error",
                confirmButtonText: lang.labels.okGotIt,
              });
            }
          })
          .catch((error) => {
            errorWebHandler(lang, error);
          });
      }
    });
  };

  const handleSuccessDeadline = (mode: "create" | "edit") => {
    if (mode === "create") {
      loadDeadlines(1);
    }
    if (mode === "edit") {
      loadDeadlines(deadlinePag.page);
    }
  };

  // ── MEASURE UNIT ACTIONS ──────────────────────────────────────────────────────────

  const executeOpenUnitCreate = () => {
    setSelectedUnit(null);
    modalShow("modal-settings-measure-unit");
  };

  const executeOpenUnitEdit = (item: MeasureUnit) => {
    setSelectedUnit(item);
    modalShow("modal-settings-measure-unit");
  };

  const executeDeleteUnit = async (item: MeasureUnit) => {
    GlobalSwal.fire({
      title: lang.labels.areYouSureYouWantToDeleteThisRecord,
      text: lang.labels.youWontBeAbleToRevertThis,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDeleteIt,
      cancelButtonText: lang.labels.cancel,
    }).then(async (result) => {
      if (companyForUser && result.isConfirmed) {
        await SettingsDA.deleteSettingMeasureUnit(companyForUser.id, item.id)
          .then((response) => {
            if (response.status === 200) {
              const prevPage = units?.length === 1 && unitPag.page > 1 ? unitPag.page - 1 : unitPag.page;
              loadUnits(prevPage);
            } else {
              errorResponse = response.data;
              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: "error",
                confirmButtonText: lang.labels.okGotIt,
              });
            }
          })
          .catch((error) => {
            errorWebHandler(lang, error);
          });
      }
    });
  };

  const handleSuccessMeasureUnit = (mode: "create" | "edit") => {
    if (mode === "create") {
      loadUnits(1);
    }
    if (mode === "edit") {
      loadUnits(unitPag.page);
    }
  };

  // ── FEATURE ACTIONS ───────────────────────────────────────────────────────

  const executeOpenFeatureCreate = () => {
    setSelectedFeature(null);
    modalShow("modal-settings-feature");
  };

  const executeOpenFeatureEdit = (item: Feature) => {
    setSelectedFeature(item);
    modalShow("modal-settings-feature");
  };

  const executeDeleteFeature = async (item: Feature) => {
    GlobalSwal.fire({
      title: lang.labels.areYouSureYouWantToDeleteThisRecord,
      text: lang.labels.youWontBeAbleToRevertThis,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDeleteIt,
      cancelButtonText: lang.labels.cancel,
    }).then(async (result) => {
      if (companyForUser && result.isConfirmed) {
        await SettingsDA.deleteSettingFeature(companyForUser.id, item.id)
          .then((response) => {
            if (response.status === 200) {
              const prevPage = features?.length === 1 && featurePag.page > 1 ? featurePag.page - 1 : featurePag.page;
              loadFeatures(prevPage);
            } else {
              errorResponse = response.data;
              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: "error",
                confirmButtonText: lang.labels.okGotIt,
              });
            }
          })
          .catch((error) => {
            errorWebHandler(lang, error);
          });
      }
    });
  };

  const handleSuccessFeature = (mode: "create" | "edit") => {
    if (mode === "create") {
      loadFeatures(1);
    }
    if (mode === "edit") {
      loadFeatures(featurePag.page);
    }
  };

  // ── DUNNING POLICY ACTIONS ────────────────────────────────────────────────

  const loadDunningPolicies = async (page: number = 1) => {
    if (companyForUser) {
      await SettingsDA.getDunningPolicies(companyForUser.id, page, policyPag.limit)
        .then((response) => {
          if (response.status === 200) {
            const data = response.data as DunningPoliciesData;
            setDunningPolicies(data.items ?? []);
            setPolicyPagData(data.pagination);
          } else {
            errorResponse = response.data;
            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: "error",
              confirmButtonText: lang.labels.okGotIt,
            });
          }
        })
        .catch((error) => {
          errorWebHandler(lang, error);
        });
    }
  };

  const executeOpenPolicyCreate = () => {
    setSelectedPolicy(null);
    modalShow("modal-dunning-policy");
  };

  const executeOpenPolicyEdit = (item: DunningPolicy) => {
    setSelectedPolicy(item);
    modalShow("modal-dunning-policy");
  };

  const executeDeletePolicy = async (item: DunningPolicy) => {
    GlobalSwal.fire({
      title: lang.labels.areYouSureYouWantToDeleteThisRecord,
      text: lang.labels.youWontBeAbleToRevertThis,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDeleteIt,
      cancelButtonText: lang.labels.cancel,
    }).then(async (result) => {
      if (companyForUser && result.isConfirmed) {
        await SettingsDA.deleteDunningPolicy(companyForUser.id, item.id)
          .then((response) => {
            if (response.status === 200) {
              const prevPage = dunningPolicies?.length === 1 && policyPag.page > 1 ? policyPag.page - 1 : policyPag.page;
              loadDunningPolicies(prevPage);
            } else {
              errorResponse = response.data;
              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: "error",
                confirmButtonText: lang.labels.okGotIt,
              });
            }
          })
          .catch((error) => {
            errorWebHandler(lang, error);
          });
      }
    });
  };

  // ── INIT ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    loadDeadlines(1);
    loadUnits(1);
    loadFeatures(1);
    loadDunningPolicies(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── RENDER ────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div className="card-title fs-4 fw-bolder">{lang.labels.companyGeneralSettings}</div>
        </div>
        {deadlines && units && features ? (
          <div className="card-body">
            {/* ── DEADLINES ─────────────────────────────────────────────── */}
            <div className="mb-10">
              <div className="d-flex bd-highlight align-items-center mb-5">
                <div className="me-auto bd-highlight">
                  <div className="fs-6 fw-bold">{lang.labels.dueDates}</div>
                </div>
                <div className="bd-highlight">
                  <button className="btn btn-sm btn-primary" type="button" onClick={executeOpenDeadlineCreate}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-5 ms-0 me-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black"></rect>
                          <rect
                            x="10.8891"
                            y="17.8033"
                            width="12"
                            height="2"
                            rx="1"
                            transform="rotate(-90 10.8891 17.8033)"
                            fill="black"
                          ></rect>
                          <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black"></rect>
                        </svg>
                      </span>
                      {lang.labels.addDeadline}
                    </div>
                  </button>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table g-1 dataTable no-footer">
                  <thead className="border-bottom border-dashed">
                    <tr className="text-muted text-uppercase fw-bolder">
                      <th className="text-start sorting_disabled">{lang.labels.type}</th>
                      <th className="text-start sorting_disabled">{lang.labels.quantity}</th>
                      <th className="w-100px text-end sorting_disabled">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  {deadlines.length > 0 ? (
                    <tbody>
                      {deadlines.map((item, index) => (
                        <tr key={index}>
                          <td>{item.type}</td>
                          <td>{item.quantity}</td>
                          <td className="text-end">
                            <button
                              className="btn btn-icon btn-flex btn-bg-light btn-active-color-primary me-2"
                              type="button"
                              onClick={() => executeOpenDeadlineEdit(item)}
                            >
                              <span className="svg-icon svg-icon-3">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path
                                    opacity="0.3"
                                    d="M21.4 8.35303L19.241 10.511L13.485 4.755L15.643 2.59595C16.0248 2.21423 16.5426 2 17.082 2C17.6214 2 18.1392 2.21423 18.521 2.59595L21.4 5.474C21.7817 5.85581 21.9959 6.37355 21.9959 6.913C21.9959 7.45245 21.7817 7.97019 21.4 8.35303ZM3.68699 21.932L9.88699 19.865L4.13099 14.109L2.06399 20.309C1.98815 20.5354 1.97703 20.7787 2.03189 21.0111C2.08674 21.2436 2.2054 21.4561 2.37449 21.6248C2.54359 21.7934 2.75609 21.9115 2.9886 21.9658C3.22112 22.0201 3.46443 22.0084 3.69099 21.932H3.68699Z"
                                    fill="black"
                                  ></path>
                                  <path
                                    d="M5.574 21.3L3.692 21.928C3.46591 22.0032 3.22334 22.0141 2.99144 21.9594C2.75954 21.9046 2.54744 21.7864 2.3789 21.6179C2.21036 21.4495 2.09202 21.2375 2.03711 21.0056C1.9822 20.7737 1.99289 20.5312 2.06799 20.305L2.696 18.422L5.574 21.3ZM4.13499 14.105L9.891 19.861L19.245 10.507L13.489 4.75098L4.13499 14.105Z"
                                    fill="black"
                                  ></path>
                                </svg>
                              </span>
                            </button>
                            <button
                              className="btn btn-icon btn-flex btn-bg-light btn-active-color-danger"
                              type="button"
                              onClick={() => executeDeleteDeadline(item)}
                            >
                              <span className="svg-icon svg-icon-3">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path
                                    d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z"
                                    fill="black"
                                  ></path>
                                  <path
                                    opacity="0.5"
                                    d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z"
                                    fill="black"
                                  ></path>
                                  <path
                                    opacity="0.5"
                                    d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z"
                                    fill="black"
                                  ></path>
                                </svg>
                              </span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr>
                        <td colSpan={3} className="text-center">
                          {lang.labels.noRecordsFound}
                        </td>
                      </tr>
                    </tbody>
                  )}
                </table>
              </div>
              <AppPagination
                pagination={deadlinePag}
                onPageChange={(page) => {
                  loadDeadlines(page);
                }}
              />
            </div>

            {/* ── MEASURE UNITS ─────────────────────────────────────────── */}
            <div className="mb-10">
              <div className="d-flex bd-highlight align-items-center mb-5">
                <div className="me-auto bd-highlight">
                  <div className="fs-6 fw-bold">{lang.labels.unitsOfMeasure}</div>
                </div>
                <div className="bd-highlight">
                  <button className="btn btn-sm btn-primary" type="button" onClick={executeOpenUnitCreate}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-5 ms-0 me-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black"></rect>
                          <rect
                            x="10.8891"
                            y="17.8033"
                            width="12"
                            height="2"
                            rx="1"
                            transform="rotate(-90 10.8891 17.8033)"
                            fill="black"
                          ></rect>
                          <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black"></rect>
                        </svg>
                      </span>
                      {lang.labels.addUnit}
                    </div>
                  </button>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table g-1 dataTable no-footer">
                  <thead className="border-bottom border-dashed">
                    <tr className="text-muted text-uppercase fw-bolder">
                      <th className="text-start sorting_disabled">{lang.labels.type}</th>
                      <th className="text-start sorting_disabled">{lang.labels.name}</th>
                      <th className="text-start sorting_disabled">{lang.labels.symbol}</th>
                      <th className="w-100px text-end sorting_disabled">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  {units.length > 0 ? (
                    <tbody>
                      {units.map((item, index) => (
                        <tr key={index}>
                          <td>{item.type}</td>
                          <td>{item.name}</td>
                          <td>{item.symbol}</td>
                          <td className="text-end">
                            <button
                              className="btn btn-icon btn-flex btn-bg-light btn-active-color-primary me-2"
                              type="button"
                              onClick={() => executeOpenUnitEdit(item)}
                            >
                              <span className="svg-icon svg-icon-3">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path
                                    opacity="0.3"
                                    d="M21.4 8.35303L19.241 10.511L13.485 4.755L15.643 2.59595C16.0248 2.21423 16.5426 2 17.082 2C17.6214 2 18.1392 2.21423 18.521 2.59595L21.4 5.474C21.7817 5.85581 21.9959 6.37355 21.9959 6.913C21.9959 7.45245 21.7817 7.97019 21.4 8.35303ZM3.68699 21.932L9.88699 19.865L4.13099 14.109L2.06399 20.309C1.98815 20.5354 1.97703 20.7787 2.03189 21.0111C2.08674 21.2436 2.2054 21.4561 2.37449 21.6248C2.54359 21.7934 2.75609 21.9115 2.9886 21.9658C3.22112 22.0201 3.46443 22.0084 3.69099 21.932H3.68699Z"
                                    fill="black"
                                  ></path>
                                  <path
                                    d="M5.574 21.3L3.692 21.928C3.46591 22.0032 3.22334 22.0141 2.99144 21.9594C2.75954 21.9046 2.54744 21.7864 2.3789 21.6179C2.21036 21.4495 2.09202 21.2375 2.03711 21.0056C1.9822 20.7737 1.99289 20.5312 2.06799 20.305L2.696 18.422L5.574 21.3ZM4.13499 14.105L9.891 19.861L19.245 10.507L13.489 4.75098L4.13499 14.105Z"
                                    fill="black"
                                  ></path>
                                </svg>
                              </span>
                            </button>
                            <button
                              className="btn btn-icon btn-flex btn-bg-light btn-active-color-danger"
                              type="button"
                              onClick={() => executeDeleteUnit(item)}
                            >
                              <span className="svg-icon svg-icon-3">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path
                                    d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z"
                                    fill="black"
                                  ></path>
                                  <path
                                    opacity="0.5"
                                    d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z"
                                    fill="black"
                                  ></path>
                                  <path
                                    opacity="0.5"
                                    d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z"
                                    fill="black"
                                  ></path>
                                </svg>
                              </span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr>
                        <td colSpan={4} className="text-center">
                          {lang.labels.noRecordsFound}
                        </td>
                      </tr>
                    </tbody>
                  )}
                </table>
              </div>
              <AppPagination
                pagination={unitPag}
                onPageChange={(page) => {
                  loadUnits(page);
                }}
              />
            </div>

            {/* ── FEATURES ──────────────────────────────────────────────── */}
            <div className="mb-10">
              <div className="d-flex bd-highlight align-items-center mb-5">
                <div className="me-auto bd-highlight">
                  <div className="fs-6 fw-bold">{lang.labels.characteristicsForServiceProduct}</div>
                </div>
                <div className="bd-highlight">
                  <button className="btn btn-sm btn-primary" type="button" onClick={executeOpenFeatureCreate}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-5 ms-0 me-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black"></rect>
                          <rect
                            x="10.8891"
                            y="17.8033"
                            width="12"
                            height="2"
                            rx="1"
                            transform="rotate(-90 10.8891 17.8033)"
                            fill="black"
                          ></rect>
                          <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black"></rect>
                        </svg>
                      </span>
                      {lang.labels.addFeature}
                    </div>
                  </button>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table g-1 dataTable no-footer">
                  <thead className="border-bottom border-dashed">
                    <tr className="text-muted text-uppercase fw-bolder">
                      <th className="text-start sorting_disabled">{lang.labels.type}</th>
                      <th className="text-start sorting_disabled">{lang.labels.name}</th>
                      <th className="text-start sorting_disabled">{lang.labels.options}</th>
                      <th className="w-100px text-end sorting_disabled">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  {features.length > 0 ? (
                    <tbody>
                      {features.map((item, index) => (
                        <tr key={index}>
                          <td>{item.type}</td>
                          <td>{item.name}</td>
                          <td>{item.options.join(", ")}</td>
                          <td className="text-end">
                            <button
                              className="btn btn-icon btn-flex btn-bg-light btn-active-color-primary me-2"
                              type="button"
                              onClick={() => executeOpenFeatureEdit(item)}
                            >
                              <span className="svg-icon svg-icon-3">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path
                                    opacity="0.3"
                                    d="M21.4 8.35303L19.241 10.511L13.485 4.755L15.643 2.59595C16.0248 2.21423 16.5426 2 17.082 2C17.6214 2 18.1392 2.21423 18.521 2.59595L21.4 5.474C21.7817 5.85581 21.9959 6.37355 21.9959 6.913C21.9959 7.45245 21.7817 7.97019 21.4 8.35303ZM3.68699 21.932L9.88699 19.865L4.13099 14.109L2.06399 20.309C1.98815 20.5354 1.97703 20.7787 2.03189 21.0111C2.08674 21.2436 2.2054 21.4561 2.37449 21.6248C2.54359 21.7934 2.75609 21.9115 2.9886 21.9658C3.22112 22.0201 3.46443 22.0084 3.69099 21.932H3.68699Z"
                                    fill="black"
                                  ></path>
                                  <path
                                    d="M5.574 21.3L3.692 21.928C3.46591 22.0032 3.22334 22.0141 2.99144 21.9594C2.75954 21.9046 2.54744 21.7864 2.3789 21.6179C2.21036 21.4495 2.09202 21.2375 2.03711 21.0056C1.9822 20.7737 1.99289 20.5312 2.06799 20.305L2.696 18.422L5.574 21.3ZM4.13499 14.105L9.891 19.861L19.245 10.507L13.489 4.75098L4.13499 14.105Z"
                                    fill="black"
                                  ></path>
                                </svg>
                              </span>
                            </button>
                            <button
                              className="btn btn-icon btn-flex btn-bg-light btn-active-color-danger"
                              type="button"
                              onClick={() => executeDeleteFeature(item)}
                            >
                              <span className="svg-icon svg-icon-3">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path
                                    d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z"
                                    fill="black"
                                  ></path>
                                  <path
                                    opacity="0.5"
                                    d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z"
                                    fill="black"
                                  ></path>
                                  <path
                                    opacity="0.5"
                                    d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z"
                                    fill="black"
                                  ></path>
                                </svg>
                              </span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr>
                        <td colSpan={4} className="text-center">
                          {lang.labels.noRecordsFound}
                        </td>
                      </tr>
                    </tbody>
                  )}
                </table>
              </div>
              <AppPagination
                pagination={featurePag}
                onPageChange={(page) => {
                  loadFeatures(page);
                }}
              />
            </div>

            {/* ── DUNNING POLICIES ───────────────────────────────────────── */}
            <div className="mb-0">
              <div className="d-flex bd-highlight align-items-center mb-5">
                <div className="me-auto bd-highlight">
                  <div className="fs-6 fw-bold">{lang.labels.dunningPolicies}</div>
                </div>
                <div className="bd-highlight">
                  <button className="btn btn-sm btn-primary" type="button" onClick={executeOpenPolicyCreate}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-5 ms-0 me-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black"></rect>
                          <rect
                            x="10.8891"
                            y="17.8033"
                            width="12"
                            height="2"
                            rx="1"
                            transform="rotate(-90 10.8891 17.8033)"
                            fill="black"
                          ></rect>
                          <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black"></rect>
                        </svg>
                      </span>
                      {lang.labels.addDunningPolicy}
                    </div>
                  </button>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table g-1 dataTable no-footer">
                  <thead className="border-bottom border-dashed">
                    <tr className="text-muted text-uppercase fw-bolder">
                      <th className="text-start sorting_disabled">{lang.labels.name}</th>
                      <th className="text-center sorting_disabled">{lang.labels.dunningSteps}</th>
                      <th className="text-center sorting_disabled">{lang.labels.setAsDefault}</th>
                      <th className="w-100px text-end sorting_disabled">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  {dunningPolicies.length > 0 ? (
                    <tbody>
                      {dunningPolicies.map((item, index) => (
                        <tr key={index}>
                          <td>{item.label}</td>
                          <td className="text-center">{item.steps?.length ?? 0}</td>
                          <td className="text-center">
                            {item.is_default && <span className="badge badge-light-success">{lang.labels.setAsDefault}</span>}
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-icon btn-flex btn-bg-light btn-active-color-primary me-2"
                              type="button"
                              onClick={() => executeOpenPolicyEdit(item)}
                            >
                              <span className="svg-icon svg-icon-3">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path
                                    opacity="0.3"
                                    d="M21.4 8.35303L19.241 10.511L13.485 4.755L15.643 2.59595C16.0248 2.21423 16.5426 2 17.082 2C17.6214 2 18.1392 2.21423 18.521 2.59595L21.4 5.474C21.7817 5.85581 21.9959 6.37355 21.9959 6.913C21.9959 7.45245 21.7817 7.97019 21.4 8.35303ZM3.68699 21.932L9.88699 19.865L4.13099 14.109L2.06399 20.309C1.98815 20.5354 1.97703 20.7787 2.03189 21.0111C2.08674 21.2436 2.2054 21.4561 2.37449 21.6248C2.54359 21.7934 2.75609 21.9115 2.9886 21.9658C3.22112 22.0201 3.46443 22.0084 3.69099 21.932H3.68699Z"
                                    fill="black"
                                  ></path>
                                  <path
                                    d="M5.574 21.3L3.692 21.928C3.46591 22.0032 3.22334 22.0141 2.99144 21.9594C2.75954 21.9046 2.54744 21.7864 2.3789 21.6179C2.21036 21.4495 2.09202 21.2375 2.03711 21.0056C1.9822 20.7737 1.99289 20.5312 2.06799 20.305L2.696 18.422L5.574 21.3ZM4.13499 14.105L9.891 19.861L19.245 10.507L13.489 4.75098L4.13499 14.105Z"
                                    fill="black"
                                  ></path>
                                </svg>
                              </span>
                            </button>
                            <button
                              className="btn btn-icon btn-flex btn-bg-light btn-active-color-danger"
                              type="button"
                              onClick={() => executeDeletePolicy(item)}
                            >
                              <span className="svg-icon svg-icon-3">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path
                                    d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z"
                                    fill="black"
                                  ></path>
                                  <path
                                    opacity="0.5"
                                    d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z"
                                    fill="black"
                                  ></path>
                                  <path
                                    opacity="0.5"
                                    d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z"
                                    fill="black"
                                  ></path>
                                </svg>
                              </span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr>
                        <td colSpan={5} className="text-center">
                          {lang.labels.noRecordsFound}
                        </td>
                      </tr>
                    </tbody>
                  )}
                </table>
              </div>
              <AppPagination
                pagination={policyPag}
                onPageChange={(page) => {
                  loadDunningPolicies(page);
                }}
              />
            </div>
          </div>
        ) : (
          <AppLoading></AppLoading>
        )}
      </div>

      <AppModalSettingsDeadline companyForUser={companyForUser} deadlineItem={selectedDeadline} onSuccess={handleSuccessDeadline} />
      <AppModalSettingsMeasureUnit companyForUser={companyForUser} unitItem={selectedUnit} onSuccess={handleSuccessMeasureUnit} />
      <AppModalSettingsFeature companyForUser={companyForUser} featureItem={selectedFeature} onSuccess={handleSuccessFeature} />
      <AppModalDunningPolicy companyForUser={companyForUser} policyToEdit={selectedPolicy} loadDunningPolicies={loadDunningPolicies} />
    </>
  );
};

export default AppCompanyUpdateGeneral;
